import { Injectable } from '@angular/core';
import { Observable, from, map, switchMap, tap } from 'rxjs';
import { AccountModel, BuyAmericanModel, CMMCLevelModel, CollaborationHistoryEnum, ContractScheduleModel, CreateOpportunityRequest, CreateOpportunityResponse, CustomerInformation, CustomerSearchRequest, CustomerSearchType, DefensivePrioritiesModel, EstimateData, EstimateInfoForOpportunity, EstimateSaveRequest, EstimateStatus, EstimateTypes, FFDropDownCollectionResponse, ForcePublishDetails, GovernmentAgencyModel, GovernmentEntityModel, LinkUnlinkOpportunityRequest, LinkUnlinkOpportunityResponse, OpenEstimateRequest, OpenEstimateResponse, OpportunityModel, OpportunityModelRequest, OpportunityRequest, OpportunityVerificationResponse, OtherMetaData, RestrictionTypeModel, SQWebProjectContainer, VerifyOpportunityRequest } from '../models/dtos';
import { ISearchCustomersData } from '../models/common/ISearchCustomersData';
import { IRadioButtonState } from '../models/common/IRadioButtonState';
import { ICmmcState } from '../models/common/ICmmcState';
import { IRestrictionTypeResult } from '../models/common/IRestrictionTypeResult';
import { IFederalFundedOutputModel } from '../models/common/IFederalFundedOutputModel';
import { IVerificationState } from '../models/common/IVerificationState';
import { CRMApiService, CreateOpportunityEvent, LinkUnlinkOpportunityEvent } from './api/crm-api.service';
import { OpenEstimateApiService } from './api/open-estimate.service';
import { CommonDataService } from './common-data.service';
import { CommonFunctionsService } from './common-functions.service';
import { EstimateSaveRequestBuilderService } from './estimate-save-request-builder.service';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../Constants/Constants';
import { CommonMetaDataService } from './common-meta-data.service';
import { TaxTypes } from '../models/sol-estimate-table/SellPriceConfig';
import { EstimateCommonFunctionsService } from './estimate/estimate-common-functions.service';
import { LoadingSpinnerService } from './common/loading-spinner.service';
import { DecimalPrecisionPipe } from '../pipes/decimal-precision-pipe/decimal-precision.pipe';
import { version } from '../../environments/environment';
import { roundHalfEvenToPrecision } from '../../types/number-extensions';

// Spinner messages shown while the CRM operations run their client-side phases.
// Once the request is in flight the server takes over via SSE status events.
const SPINNER_MESSAGE_PREPARING_REQUEST = 'Preparing request...';
const SPINNER_MESSAGE_CREATING_SAVE_REQUEST = 'Creating save request...';
const SPINNER_MESSAGE_AWAITING_RESPONSE = 'Awaiting response...';

@Injectable({
  providedIn: 'root',
})
export class CrmOperationsService {

  private openEstimateResponse: OpenEstimateResponse | null = null;

  /**
   * Resets cached estimate response.
   * Called when the user closes an estimate/project.
   * Add additional fields if there are any in future
   */
  resetEstimateState(): void {
    this.clearOpenEstimateResponse();
  }

  syncEstimateDataIfOpen(linkUnlinkResponse: LinkUnlinkOpportunityResponse): void {
    const projectContainer = linkUnlinkResponse?.projectContainer ?? null;
    const estimateData = this.commonDataService.getEstimateData();
    if (!projectContainer || !estimateData) {
      return;
    }
    this.commonDataService.publishProjectContainer(projectContainer);
    this.commonDataService.patchEstimate({ isECS: linkUnlinkResponse.isECS });
    const opportunityRelationshipData = this.commonDataService.getOpportunityRelationshipData();
    if (opportunityRelationshipData) {
      opportunityRelationshipData.opportunityLink = linkUnlinkResponse.opportunityLink ?? CONSTANTS.EMPTY_STRING;
      opportunityRelationshipData.opportunityID = projectContainer.metaData?.estimateMetaData?.estimateSettings?.opportunityId ?? CONSTANTS.EMPTY_STRING;
    }
    if (this.commonDataService.isCollaborationEstimate() && linkUnlinkResponse?.collaborationHubData) {
      const collaborationHubData = linkUnlinkResponse?.collaborationHubData;
      this.commonFunctions.updateCollaborationHubData(collaborationHubData);
    }
    this.commonDataService.isSaveEnabled = false;
  }

  constructor(private readonly commonDataService: CommonDataService,
    private readonly crmApiService: CRMApiService,
    private readonly openEstimateService: OpenEstimateApiService,
    private readonly commonFunctions: CommonFunctionsService,
    private readonly estimateSaveRequestBuilderService: EstimateSaveRequestBuilderService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly commonCalcFunctions: EstimateCommonFunctionsService,
    private readonly translate: TranslateService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe
  ) { }

  // #region Public

  getEstimateInfoForOpportunity(): EstimateInfoForOpportunity {
    const estimateData = structuredClone(this.commonDataService.getEstimateData());
    if (estimateData) {
      return this.mapEstimateDataToInfo(estimateData);
    }
    const info = new EstimateInfoForOpportunity();
    info.status = EstimateStatus.Active;
    info.type = EstimateTypes.BaseEstimate;
    info.startDate = new Date();
    info.endDate = new Date();
    return info;
  }

  getUSInfoDDLists() {
    return this.crmApiService.getUSInfoDDLists();
  }

  getEntityItems(data: FFDropDownCollectionResponse): Array<{ label: string; value: string }> {
    return (data.governmentEntities ?? []).map((e: GovernmentEntityModel) => ({
      label: e.governmentEntityFunded,
      value: e.governmentEntityFunded,
    }));
  }

  getDefaultEntity(data: FFDropDownCollectionResponse): string | null {
    const def = (data.governmentEntities ?? []).find((e: GovernmentEntityModel) => e.isDefault);
    return def ? def.governmentEntityFunded : null;
  }

  getAgencyDropdownItems(data: FFDropDownCollectionResponse, entity: string): { items: Array<{ label: string; value: string }>; disabled: boolean } {
    const allFiltered = (data.governmentAgencies ?? [])
      .filter((a: GovernmentAgencyModel) => this.ciEquals(a.governmentEntityFunded, entity));
    const isDisabled = allFiltered.some((a: GovernmentAgencyModel) => this.ciEquals(a.governmentAgency, CONSTANTS.DISABLED));
    const items = allFiltered
      .filter((a: GovernmentAgencyModel) => !this.ciEquals(a.governmentAgency, CONSTANTS.DISABLED))
      .map((a: GovernmentAgencyModel) => ({ label: a.governmentAgency, value: a.governmentAgency }));
    return { items, disabled: isDisabled };
  }

  getContractScheduleDropdownItems(data: FFDropDownCollectionResponse, entity: string): { items: Array<{ label: string; value: string }>; disabled: boolean } {
    const all = (data.contractSchedules ?? [])
      .filter((c: ContractScheduleModel) => this.ciEquals(c.governmentEntityFunded, entity));
    const isDisabled = all.some((c: ContractScheduleModel) => this.ciEquals(c.contractSchedule, CONSTANTS.DISABLED));
    const items = all
      .filter((c: ContractScheduleModel) => !this.ciEquals(c.contractSchedule, CONSTANTS.DISABLED))
      .map((c: ContractScheduleModel) => ({ label: c.contractSchedule, value: c.contractSchedule }));
    return { items, disabled: isDisabled };
  }

  getBuyAmericanState(data: FFDropDownCollectionResponse, entity: string): IRadioButtonState {
    return this.evaluateYesNoRadio<BuyAmericanModel>(data.buyAmericans ?? [], entity, 'buyAmerican');
  }

  getDpasState(data: FFDropDownCollectionResponse, entity: string): IRadioButtonState {
    return this.evaluateYesNoRadio<DefensivePrioritiesModel>(data.defensivePriorities ?? [], entity, 'dpas', [CONSTANTS.DISABLED, CONSTANTS.GREYED_OUT]);
  }

  getCmmcState(data: FFDropDownCollectionResponse, entity: string, agency?: string | null): ICmmcState {
    const filtered = (data.cmmcLevels ?? [])
      .filter((c: CMMCLevelModel) => this.ciEquals(c.governmentEntityFunded, entity) && (!agency || this.ciEquals(c.govtAgency, agency)));

    if (filtered.length === 0) {
      return { cuiEnabled: false, fciEnabled: false, checked: null };
    }

    let isDisabled = false;
    let hasCui = false;
    let hasFci = false;
    // let defaultRow: CMMCLevelModel | undefined;

    for (const c of filtered) {
      if (this.ciEquals(c.cmmcLevel, CONSTANTS.DISABLED)) {
        isDisabled = true;
        break;
      }
      if (this.ciEquals(c.cmmcLevel, CONSTANTS.CMMC_LEVEL_CUI)) hasCui = true;
      if (this.ciEquals(c.cmmcLevel, CONSTANTS.CMMC_LEVEL_FCI)) hasFci = true;
      // if (c.default) defaultRow = c;
    }

    if (isDisabled) {
      return { cuiEnabled: false, fciEnabled: false, checked: null };
    }

    let checked: 'Level 2 - CUI' | 'Level 1 - FCI' | null = null;
    // if (defaultRow) {
    //   if (this.ciEquals(defaultRow.cmmcLevel, CONSTANTS.CMMC_LEVEL_CUI)) {
    //     checked = CONSTANTS.CMMC_LEVEL_CUI;
    //   } else if (this.ciEquals(defaultRow.cmmcLevel, CONSTANTS.CMMC_LEVEL_FCI)) {
    //     checked = CONSTANTS.CMMC_LEVEL_FCI;
    //   }
    // }
    if (hasCui && hasFci) {
      checked = CONSTANTS.CMMC_LEVEL_FCI;
    }
    else if (hasCui) {
      checked = CONSTANTS.CMMC_LEVEL_CUI;
    }
    else if (hasFci) {
      checked = CONSTANTS.CMMC_LEVEL_FCI;
    }

    return { cuiEnabled: hasCui, fciEnabled: hasFci, checked };
  }

  getRestrictionTypeResult(data: FFDropDownCollectionResponse, entity: string, contractSchedule: string | null): IRestrictionTypeResult {
    if (!contractSchedule) {
      return { restrictionTypes: [], defaultRestriction: null, isEditable: true };
    }

    const filtered = (data.restrictionTypes ?? []).filter((r: RestrictionTypeModel) =>
      this.ciEquals(r.governmentEntityFunded, entity)
      && (this.ciEquals(r.contractSchedule, contractSchedule) || this.ciEquals(r.contractSchedule, CONSTANTS.DISABLED))
    );

    const restrictionTypes = [...new Set(filtered.map(r => r.restrictionType))];

    switch (filtered.length) {
      case CONSTANTS.ONE:
        return { restrictionTypes, defaultRestriction: filtered[CONSTANTS.ZERO].restrictionType, isEditable: false };
      case CONSTANTS.TWO: {
        const defaultRow = filtered.find(r => r.default);
        return { restrictionTypes, defaultRestriction: defaultRow?.restrictionType ?? null, isEditable: true };
      }
      default:
        return { restrictionTypes, defaultRestriction: null, isEditable: true };
    }
  }

  convertSearchDataToCustomerInfo(searchData: ISearchCustomersData): CustomerInformation {
    return this.buildCustomerInfo(
      searchData.salesForceId || CONSTANTS.EMPTY_STRING,
      searchData.sapNumber || CONSTANTS.EMPTY_STRING,
      searchData.customerName || CONSTANTS.EMPTY_STRING,
      searchData.address1 || CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING,
      searchData.city || CONSTANTS.EMPTY_STRING,
      searchData.state || CONSTANTS.EMPTY_STRING,
      searchData.zipCode || CONSTANTS.EMPTY_STRING
    );
  }

  verifyCustomerById(accountId: string, searchType: CustomerSearchType): Observable<CustomerInformation | null> {
    const request: CustomerSearchRequest = {
      accountModelRequest: {
        accountID: accountId,
        customerID: null,
        accountName: null,
        shippingAddress1: null,
        shippingCity: null,
        shippingStateProvince: null,
        shippingZipCode: null
      },
      customerSearchType: searchType,
      cultureId: CONSTANTS.DEFAULT_CULTURE_ID
    };

    return this.crmApiService.searchCustomers(request).pipe(
      map(response => {
        if (response?.accountModels?.length > 0) {
          const account = response.accountModels[0];
          return this.buildCustomerInfo(
            account.accountID || CONSTANTS.EMPTY_STRING,
            account.customerID || CONSTANTS.EMPTY_STRING,
            account.accountName || CONSTANTS.EMPTY_STRING,
            account.shippingAddress1 || CONSTANTS.EMPTY_STRING,
            CONSTANTS.EMPTY_STRING,
            account.shippingCity || CONSTANTS.EMPTY_STRING,
            account.shippingStateProvince || CONSTANTS.EMPTY_STRING,
            account.shippingZipCode || CONSTANTS.EMPTY_STRING
          );
        }
        return null;
      })
    );
  }

  verifyEstimateId(estimateId: string, userEmailId: string): Observable<EstimateInfoForOpportunity> {
    const payload: OpenEstimateRequest = {
      userId: this.commonDataService.getUserId() || CONSTANTS.EMPTY_STRING,
      estimateId,
      userEmailId,
      culture: this.commonDataService.getCurrentApplicationCulture(),
      isArchive: false
    };
    return this.openEstimateService.openEstimate(payload).pipe(
      tap(response => this.openEstimateResponse = response),
      map(response => this.mapEstimateDataToInfo(response.estimateData))
    );
  }

  getOpenEstimateResponse(): OpenEstimateResponse | null {
    return this.openEstimateResponse;
  }

  clearOpenEstimateResponse(): void {
    this.openEstimateResponse = null;
  }

  getOtherMetaData(): OtherMetaData | null {
    return structuredClone(this.commonDataService.getOtherMetaData()) ?? null;
  }

  getOtherMetaDataFromResponse(): OtherMetaData | null {
    return this.openEstimateResponse?.estimateData.projectObject.metaData.otherMetaData ?? null;
  }

  verifyOpportunityCanLink(request: VerifyOpportunityRequest): Observable<OpportunityVerificationResponse> {
    return this.crmApiService.verifyOpportunityCanLink(request);
  }

  getOpportunity(request: OpportunityRequest): Observable<OpportunityModel> {
    return this.crmApiService.getOpportunity(request);
  }

  convertAccountToCustomerInfo(account: AccountModel): CustomerInformation {
    return this.buildCustomerInfo(
      account.accountID || CONSTANTS.EMPTY_STRING,
      account.customerID || CONSTANTS.EMPTY_STRING,
      account.accountName || CONSTANTS.EMPTY_STRING,
      account.shippingAddress1 || CONSTANTS.EMPTY_STRING,
      account.shippingAddress2 || CONSTANTS.EMPTY_STRING,
      account.shippingCity || CONSTANTS.EMPTY_STRING,
      account.shippingStateProvince || CONSTANTS.EMPTY_STRING,
      account.shippingZipCode || CONSTANTS.EMPTY_STRING
    );
  }

  linkOpportunity(
    estimateInfo: EstimateInfoForOpportunity,
    opportunityModel: OpportunityModel,
    userId: string,
    userName: string,
    userEmail: string
  ): Observable<LinkUnlinkOpportunityEvent> {
    return from(this.buildLinkUnlinkRequest(estimateInfo, opportunityModel, userId, userName, userEmail, true)).pipe(
      switchMap(request => {
        this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_AWAITING_RESPONSE);
        return this.crmApiService.linkOpportunity(request);
      })
    );
  }

  unlinkOpportunity(
    estimateInfo: EstimateInfoForOpportunity,
    opportunityModel: OpportunityModel,
    userId: string,
    userName: string,
    userEmail: string
  ): Observable<LinkUnlinkOpportunityEvent> {
    return from(this.buildLinkUnlinkRequest(estimateInfo, opportunityModel, userId, userName, userEmail, false)).pipe(
      switchMap(request => {
        this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_AWAITING_RESPONSE);
        return this.crmApiService.unlinkOpportunity(request);
      })
    );
  }

  createOpportunity(params: {
    estimateInfo: EstimateInfoForOpportunity;
    opportunityName: string;
    opportunityDescription: string;
    expectedBook: Date;
    sellAs: string;
    ecs: string;
    restrictionType: string;
    isChangeOrder: boolean;
    baseOpportunityId: string;
    baseOpportunity: OpportunityModel | null;
    soldToCustomerInfo: CustomerInformation;
    shipToCustomerInfo: CustomerInformation;
    billToCustomerInfo: CustomerInformation;
    endUserCustomerInfo: CustomerInformation;
    federalFundedOutput: IFederalFundedOutputModel;
    userId: string;
    userEmail: string;
  }): Observable<CreateOpportunityEvent> {
    return from(this.buildCreateOpportunityRequest(params)).pipe(
      switchMap(request => {
        this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_AWAITING_RESPONSE);
        return this.crmApiService.createOpportunity(request);
      })
    );
  }

  private async buildLinkUnlinkRequest(
    estimateInfo: EstimateInfoForOpportunity,
    opportunityModel: OpportunityModel,
    userId: string,
    userName: string,
    userEmail: string,
    isLink: boolean
  ): Promise<LinkUnlinkOpportunityRequest> {
    this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_PREPARING_REQUEST);

    const accounts = opportunityModel.accounts ?? [];
    const soldTo = accounts.find(a => a?.customerTypeName === CONSTANTS.SOLD_TO) ?? null;
    const shipTo = accounts.find(a => a?.customerTypeName === CONSTANTS.SHIP_TO) ?? null;
    const billTo = accounts.find(a => a?.customerTypeName === CONSTANTS.BILL_TO) ?? null;
    const endUser = accounts.find(a => a?.customerTypeName === CONSTANTS.END_USER) ?? null;

    const estimateSaveRequest = await this.buildEstimateSaveRequestForLink(userId, userEmail);

    return {
      loggedInUserName: userName,
      loggedInUserId: userId,
      opportunityId: opportunityModel.opportunityId,
      opportunityName: opportunityModel.opportunityName,
      customerInfo: soldTo,
      shipToAccount: shipTo,
      billToAccount: billTo,
      endUserAccount: endUser,
      plSegment: opportunityModel.plSegment,
      plSubSegment: opportunityModel.plSubSegment,
      opportunityGuid: opportunityModel.opportunityGuid,
      salesType: opportunityModel.salesType,
      isCUI: opportunityModel.isCUI,
      cultureId: CONSTANTS.DEFAULT_CULTURE_ID,
      isRestrictedOpportunity: opportunityModel.isRestrictedOpportunity,
      isAdditionalCUIRestrictionsApplied: false,
      restrictedType: estimateInfo.restrictedType ?? CONSTANTS.DEFAULT_RESTRICTION_TYPE,
      estimateSaveRequest,
      forcePublishDetails: isLink
        ? this.getForcePublishDetailsForLink(opportunityModel.opportunityId)
        : this.getForcePublishDetailsForUnlink(opportunityModel.opportunityId),
      isOpportunityECS: opportunityModel.bsesNationalAccts === CONSTANTS.Y
    };
  }

  private async buildCreateOpportunityRequest(params: {
    estimateInfo: EstimateInfoForOpportunity;
    opportunityName: string;
    opportunityDescription: string;
    expectedBook: Date;
    sellAs: string;
    ecs: string;
    restrictionType: string;
    isChangeOrder: boolean;
    baseOpportunityId: string;
    baseOpportunity: OpportunityModel | null;
    soldToCustomerInfo: CustomerInformation;
    shipToCustomerInfo: CustomerInformation;
    billToCustomerInfo: CustomerInformation;
    endUserCustomerInfo: CustomerInformation;
    federalFundedOutput: IFederalFundedOutputModel;
    userId: string;
    userEmail: string;
  }): Promise<CreateOpportunityRequest> {
    this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_PREPARING_REQUEST);

    const info = params.estimateInfo;
    const ff = params.federalFundedOutput;

    const isRestricted = (params.restrictionType === CONSTANTS.RESTRICTED_INDICATOR.toLowerCase()
      || params.restrictionType === CONSTANTS.YES.toLowerCase()
      || params.restrictionType === CONSTANTS.RESTRICTED_TYPE);

    const isCUI = ff.cmmcLevel === CONSTANTS.CMMC_LEVEL_CUI;

    const costReviewData = this.openEstimateResponse ? this.openEstimateResponse.costReviewDatabaseRecord : this.commonDataService.getCostReviewData();
    const cultureId = this.getCultureId();
    const opportunityModelRequest: OpportunityModelRequest = {
      opportunityId: null,
      opportunityName: params.opportunityName,
      siteID: info.siteID || null,
      revenue: roundHalfEvenToPrecision(info.totalSellPrice, CONSTANTS.TWO).toString(),
      closeDate: params.expectedBook ? this.formatDateForGateway(params.expectedBook) : null,
      plSegment: info.businessUnit,
      ltMechanicalIssuesMitigation: CONSTANTS.EMPTY_STRING,
      plSalesOffice: info.salesOffice,
      plSubSegment: info.subSegment,
      dProjectStartDate: info.startDate ? this.formatDateForGateway(new Date(info.startDate)) : null,
      dProjectEndDate: info.endDate ? this.formatDateForGateway(new Date(info.endDate)) : null,
      cEstimateSalePrice: params.sellAs,
      pcGrossMarginPercent: info.gmPercentage?.toString() ?? CONSTANTS.EMPTY_STRING,
      pcEstimateGM: info.gmPercentage?.toString() ?? CONSTANTS.EMPTY_STRING,
      plIsCustomerOrEndUserFederalEntity: null,
      stSAPJobNumber: null,
      bsesNationalAccts: params.ecs === CONSTANTS.YES ? CONSTANTS.Y : CONSTANTS.N,
      plGSASchedule: ff.contractSchedule || null,
      stQuotationNumber: CONSTANTS.ZERO_STRING,
      governmentAgency: ff.govtAgency || null,
      governmentEntity: ff.govtEntity || CONSTANTS.EMPTY_STRING,
      accountId: params.soldToCustomerInfo.customerSalesforceId ?? CONSTANTS.EMPTY_STRING,
      opportunityLegacyId: null,
      soldToSAPNumber: params.soldToCustomerInfo.customerID ?? CONSTANTS.EMPTY_STRING,
      shipToSAPNumber: params.shipToCustomerInfo.customerID ?? CONSTANTS.EMPTY_STRING,
      endUserSAPNumber: params.endUserCustomerInfo.customerID ?? CONSTANTS.EMPTY_STRING,
      endUserAccountId: params.endUserCustomerInfo.customerSalesforceId ?? CONSTANTS.EMPTY_STRING,
      shipToAccountId: params.shipToCustomerInfo.customerSalesforceId ?? CONSTANTS.EMPTY_STRING,
      isRestrictedOpportunity: isRestricted ? CONSTANTS.Y : CONSTANTS.N,
      buyAmerican: ff.buyAmerican || null,
      opportunityDescription: params.opportunityDescription || null,
      opportunityGuid: null,
      billToSAPNumber: params.billToCustomerInfo.customerID ?? CONSTANTS.EMPTY_STRING,
      billToAccountId: params.billToCustomerInfo.customerSalesforceId ?? CONSTANTS.EMPTY_STRING,
      salesType: null,
      reasonForException: CONSTANTS.EMPTY_STRING,
      isCostReviewRequired: this.commonDataService.getReviewTabVisibility(),
      costReviewStatus: this.commonFunctions.getCostReviewStatusName(costReviewData),
      isCUI,
      usSpecificInformation: cultureId === CONSTANTS.ONE ? {
        govtEntity: ff.govtEntity || CONSTANTS.EMPTY_STRING,
        govtAgency: ff.govtAgency || null,
        contractSchedule: ff.contractSchedule || null,
        buyAmerican: ff.buyAmerican || null,
        dpas: ff.dpas || null,
        cmmcLevel: ff.cmmcLevel || null
      } : null,
      isChangeOrder: params.isChangeOrder,
      accounts: null
    };

    let baseOpportunitySubSegment: string | null = null;
    if (params.isChangeOrder) {
      const baseOpportunity = params.baseOpportunity;
      opportunityModelRequest.opportunityId = params.baseOpportunityId;
      opportunityModelRequest.stSAPJobNumber = baseOpportunity?.stSAPJobNumber ?? null;
      opportunityModelRequest.bsesNationalAccts = baseOpportunity?.bsesNationalAccts ?? CONSTANTS.N;
      opportunityModelRequest.governmentAgency = baseOpportunity?.governmentAgency || null;
      opportunityModelRequest.governmentEntity = baseOpportunity?.governmentEntity || CONSTANTS.EMPTY_STRING;
      opportunityModelRequest.accountId = baseOpportunity?.accountId ?? CONSTANTS.EMPTY_STRING;
      opportunityModelRequest.soldToSAPNumber = baseOpportunity?.soldToSAPNumber ?? CONSTANTS.EMPTY_STRING;
      opportunityModelRequest.isRestrictedOpportunity = baseOpportunity?.isRestrictedOpportunity === CONSTANTS.Y
        || baseOpportunity?.isRestrictedOpportunity === CONSTANTS.N
        ? baseOpportunity.isRestrictedOpportunity
        : isRestricted ? CONSTANTS.Y : CONSTANTS.N;
      opportunityModelRequest.ltMechanicalIssuesMitigation = (params.opportunityDescription || '').slice(0, 255);
      opportunityModelRequest.plIsCustomerOrEndUserFederalEntity = baseOpportunity?.plIsCustomerOrEndUserFederalEntity ?? null;
      baseOpportunitySubSegment = baseOpportunity?.plSubSegment ?? '';
    } else {
      opportunityModelRequest.ltMechanicalIssuesMitigation = CONSTANTS.EMPTY_STRING;
      opportunityModelRequest.plIsCustomerOrEndUserFederalEntity = null;
    }

    const estimateSaveRequest = await this.buildEstimateSaveRequestForLink(params.userId, params.userEmail);

    return {
      cultureId: this.getCultureId(),
      estimateId: info.estimateId,
      isEstimateModified: this.commonDataService.isSaveEnabled,
      isAdditionalCUIRestrictionsApplied: isCUI,
      soldToCustomerInfo: this.buildAccountModelFromCustomerInfo(params.soldToCustomerInfo, CONSTANTS.SOLD_TO),
      baseOpportunitySubSegment,
      shipToAccount: this.buildAccountModelFromCustomerInfo(params.shipToCustomerInfo, CONSTANTS.SHIP_TO),
      billToAccount: this.buildAccountModelFromCustomerInfo(params.billToCustomerInfo, CONSTANTS.BILL_TO),
      endUserAccount: this.buildAccountModelFromCustomerInfo(params.endUserCustomerInfo, CONSTANTS.END_USER),
      opportunityModelRequest,
      estimateSaveRequest,
      forcePublishDetails: this.getForcePublishDetailsForCreate()
    };
  }

  private getForcePublishDetailsForLink(opportunityId: string): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    const estimateData = this.commonDataService.getEstimateData();
    const isSubmitted = estimateData?.estimateStatusId === EstimateStatus.Submitted;
    return this.buildForcePublishDetails({
      forcePublishAdditionalInfoKey: CONSTANTS.CRM_ESTIMATE_FORCE_PUBLISH_LINKING_OPPORTUNITY,
      operation: CollaborationHistoryEnum.OpportunityLinked,
      modificationNotesKey: 'CRM_SYSTEM_PUBLISHED_LINK_OPPORTUNITY',
      modificationNotesParams: { opportunityId },
      estimateCheckoutAdditionalInfoKey: CONSTANTS.CRM_ESTIMATE_ADD_ON_OPPORTUNITY_LINK,
      isEstimateBeingCheckedOutAfterPublish: !isSubmitted
    });
  }

  private getForcePublishDetailsForUnlink(opportunityId: string): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    const estimateData = this.commonDataService.getEstimateData();
    if (estimateData?.estimateStatusId === EstimateStatus.Submitted) {
      return null;
    }
    return this.buildForcePublishDetails({
      forcePublishAdditionalInfoKey: CONSTANTS.CRM_ESTIMATE_FORCE_PUBLISH_UNLINKING_OPPORTUNITY,
      operation: CollaborationHistoryEnum.OpportunityUnlinked,
      modificationNotesKey: 'CRM_SYSTEM_PUBLISHED_UNLINK_OPPORTUNITY',
      modificationNotesParams: { opportunityId },
      estimateCheckoutAdditionalInfoKey: CONSTANTS.CRM_ESTIMATE_ADD_ON_OPPORTUNITY_UNLINK,
      isEstimateBeingCheckedOutAfterPublish: true
    });
  }

  private getForcePublishDetailsForCreate(): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    return this.buildForcePublishDetails({
      forcePublishAdditionalInfoKey: CONSTANTS.CRM_ESTIMATE_FORCE_PUBLISH_CREATE_OPPORTUNITY,
      operation: CollaborationHistoryEnum.OpportunityCreated,
      modificationNotesKey: 'CRM_SYSTEM_PUBLISHED_CREATE_OPPORTUNITY',
      estimateCheckoutAdditionalInfoKey: CONSTANTS.EMPTY_STRING,
      isEstimateBeingCheckedOutAfterPublish: true
    });
  }

  private buildForcePublishDetails(params: {
    forcePublishAdditionalInfoKey: string;
    operation: CollaborationHistoryEnum;
    modificationNotesKey: string;
    modificationNotesParams?: Record<string, unknown>;
    estimateCheckoutAdditionalInfoKey: string;
    isEstimateBeingCheckedOutAfterPublish: boolean;
  }): ForcePublishDetails {
    const forcePublishDetails: ForcePublishDetails = {
      forcePublishAdditionalInfo: params.forcePublishAdditionalInfoKey,
      operation: params.operation,
      modificationNotes: this.translate.instant(params.modificationNotesKey, params.modificationNotesParams),
      estimateCheckoutAdditionalInfo: params.estimateCheckoutAdditionalInfoKey,
      isEstimateBeingCheckedOutAfterPublish: params.isEstimateBeingCheckedOutAfterPublish
    };

    return forcePublishDetails;
  }

  private buildAccountModelFromCustomerInfo(customerInfo: CustomerInformation, customerTypeName: string): AccountModel {
    return {
      accountID: customerInfo.customerSalesforceId ?? CONSTANTS.EMPTY_STRING,
      customerID: customerInfo.customerID ?? CONSTANTS.EMPTY_STRING,
      accountName: customerInfo.customerName ?? CONSTANTS.EMPTY_STRING,
      shippingAddress1: customerInfo.custAddress1 ?? CONSTANTS.EMPTY_STRING,
      shippingCity: customerInfo.custCity ?? CONSTANTS.EMPTY_STRING,
      shippingStateProvince: customerInfo.custState ?? CONSTANTS.EMPTY_STRING,
      shippingZipCode: customerInfo.custZipCode ?? CONSTANTS.EMPTY_STRING,
      customerTypeName,
      shippingAddress2: customerInfo.custAddress2 ?? CONSTANTS.EMPTY_STRING,
      accountAddressType: CONSTANTS.EMPTY_STRING
    };
  }

  buildFederalFundedOutput(values: {
    governmentEntity: string;
    governmentAgency: string;
    contractSchedule: string;
    buyAmerican: string;
    defensivePriorities: string;
    cmmc: string;
    restrictionType: string;
  }): IFederalFundedOutputModel {
    return {
      govtEntity: values.governmentEntity || null,
      govtAgency: values.governmentAgency || null,
      contractSchedule: values.contractSchedule || null,
      buyAmerican: (values.buyAmerican as 'Yes' | 'No') || null,
      dpas: (values.defensivePriorities as 'Yes' | 'No') || null,
      cmmcLevel: (values.cmmc as 'Level 2 - CUI' | 'Level 1 - FCI') || null,
      restrictionType: values.restrictionType || null,
    };
  }

  validateCanCreate(params: {
    isFormDisabled: boolean;
    estimateId: string;
    accountName: string;
    opportunityName: string;
    businessLine: string;
    salesOffice: string;
    segment: string;
    businessHierarchy: string;
    expectedBook: Date | null;
    shipToLocation: string;
    endUser: string;
    billTO: string;
    isChangeOrder: boolean;
    baseOpportunityId: string;
    isBaseOpportunityVerified: boolean;
    enabledDropdowns: { governmentEntity: boolean; governmentAgency: boolean; contractSchedule: boolean };
    dropdownValues: { governmentEntity: string; governmentAgency: string; contractSchedule: string };
    verifiedFields: IVerificationState;
  }): boolean {
    if (params.isFormDisabled) {
      return false;
    }

    // Change order requires a verified base opportunity
    if (params.isChangeOrder && (!params.baseOpportunityId || !params.isBaseOpportunityVerified)) {
      return false;
    }

    return this.validateRequiredFields(params)
      && this.validateBookDate(params.expectedBook)
      && this.validateFederalDropdowns(params.enabledDropdowns, params.dropdownValues)
      && this.validateCustomerVerification(params);
  }

  isEstimateOpened(): boolean {
    return !!this.commonDataService.getEstimateData();
  }

  isEstimateSubmitted(): boolean {
    const estimateData = this.commonDataService.getEstimateData() ?? this.openEstimateResponse?.estimateData;
    return !!(estimateData && estimateData.estimateStatusId === EstimateStatus.Submitted);
  }

  getCultureId(): number {
    const cultureString = this.commonDataService.getUserLanguagePreference();
    return cultureString ? this.commonFunctions.getCultureId(cultureString) : CONSTANTS.ONE;
  }

  // #endregion

  // #region Private

  /**
   * Builds an EstimateSaveRequest from whichever source has the estimate data.
   * Variant 1: Estimate opened via main app — commonDataService has the data.
   * Variant 2: Estimate opened only for CRM dialogs — openEstimateResponse has the data.
    */
  private async buildEstimateSaveRequestForLink(userId: string, userEmail: string): Promise<EstimateSaveRequest> {

    this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_CREATING_SAVE_REQUEST);

    if (this.openEstimateResponse) {
      const estimateData = this.openEstimateResponse.estimateData;
      const projectContainer = estimateData.projectObject;
      projectContainer.metaData.estimateMetaData.estimateSettings.version = version;
      const costReviewData = this.openEstimateResponse.costReviewDatabaseRecord;
      const request = this.estimateSaveRequestBuilderService.buildEstimateSaveRequest(estimateData, projectContainer, costReviewData);
      request.userId = userId;
      request.userEmailId = userEmail;
      return request;
    }

    const request = await this.estimateSaveRequestBuilderService.createSaveEstimateRequest();
    request.userId = userId;
    request.userEmailId = userEmail;
    return request;
  }

  private buildCustomerInfo(
    salesforceId: string,
    customerId: string,
    name: string,
    address1: string,
    address2: string,
    city: string,
    state: string,
    zipCode: string
  ): CustomerInformation {
    const info = new CustomerInformation();
    info.customerSalesforceId = salesforceId;
    info.customerID = customerId;
    info.customerName = name;
    info.custAddress1 = address1;
    info.custAddress2 = address2;
    info.custCity = city;
    info.custState = state;
    info.custZipCode = zipCode;
    return info;
  }

  private validateRequiredFields(params: {
    estimateId: string; accountName: string; opportunityName: string;
    businessLine: string; salesOffice: string; segment: string;
    businessHierarchy: string; verifiedFields: IVerificationState;
  }): boolean {
    return !!(params.estimateId && params.accountName && params.opportunityName
      && params.businessLine && params.salesOffice && params.segment
      && params.businessHierarchy && params.verifiedFields.estimateId && params.verifiedFields.accountName);
  }

  private validateBookDate(expectedBook: Date | null): boolean {
    if (!expectedBook) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expectedBook >= today;
  }

  private validateFederalDropdowns(
    enabled: { governmentEntity: boolean; governmentAgency: boolean; contractSchedule: boolean },
    values: { governmentEntity: string; governmentAgency: string; contractSchedule: string }
  ): boolean {
    if (enabled.governmentEntity && !values.governmentEntity) return false;
    if (enabled.governmentAgency && !values.governmentAgency) return false;
    if (enabled.contractSchedule && !values.contractSchedule) return false;
    return true;
  }

  private validateCustomerVerification(params: {
    shipToLocation: string; endUser: string; billTO: string; verifiedFields: IVerificationState;
  }): boolean {
    if (params.shipToLocation && !params.verifiedFields.shipToLocation) return false;
    if (params.endUser && !params.verifiedFields.endUser) return false;
    if (params.billTO && !params.verifiedFields.billTO) return false;
    return true;
  }

  private mapEstimateDataToInfo(data: EstimateData): EstimateInfoForOpportunity {
    const info = new EstimateInfoForOpportunity();
    info.estimateId = data.estimateID;
    info.estimateName = data.estimateName;
    info.description = data.description;
    info.status = data.estimateStatusId as EstimateStatus;
    info.type = data.estimateTypeId as EstimateTypes;
    info.author = data.author;
    info.salesOfficeID = data.salesOfficeId ?? CONSTANTS.ZERO;
    const salesOfficeDetails = this.commonMetaDataService.getSalesOfficeDetails(info.salesOfficeID);
    info.salesOffice = salesOfficeDetails?.salesOfficeName;
    info.businessUnitID = data.businessUnitID ?? CONSTANTS.ZERO;
    const businessUnitDetails = this.commonMetaDataService.getBusinessUnitDetails(info.businessUnitID);
    info.businessUnit = businessUnitDetails?.businessUnit;
    info.segmentID = data.profitCenterId ?? CONSTANTS.ZERO;
    const segmentDetails = this.commonMetaDataService.getSegmentDetails(info.segmentID);
    info.segment = segmentDetails?.zweigName;
    info.subSegmentID = data.subSegmentID ?? CONSTANTS.ZERO;
    const subSegmentDetails = this.commonMetaDataService.getSubSegmentDetails(info.subSegmentID);
    if (subSegmentDetails) {
      info.subSegment = subSegmentDetails.subSegment;
      info.gckCode = subSegmentDetails.gckCode;
    }
    info.startDate = data.startDate ?? new Date();
    info.endDate = data.endDate ?? new Date();
    info.totalSellPrice = this.commonCalcFunctions.calculateOpportunitySellPrice(data);
    info.baseEstimateId = data.baseEstimateId;
    info.owner = data.owner;
    info.opportunityId = data.projectObject.metaData.estimateMetaData.estimateSettings.opportunityId;

    const isFixedPriceSelected = data.projectObject?.metaData?.estimateMetaData?.estimateTab?.isFixedPriceSelected;
    if (isFixedPriceSelected === true) {
      info.gmPercentage = CONSTANTS.ZERO;
    } else if (data.isMarkUpSelected) {
      info.gmPercentage = roundHalfEvenToPrecision(this.commonCalcFunctions.getGrossMarginPercent(data.marginPercent), CONSTANTS.TWO);
    } else {
      info.gmPercentage = data.marginPercent;
    }
    info.isECS = data.isECS;
    return info;
  }

  private ciEquals(a: string | null | undefined, b: string): boolean {
    return (a ?? '').toLowerCase() === b.toLowerCase();
  }

  private evaluateYesNoRadio<T extends { governmentEntityFunded: string }>(
    items: T[],
    entity: string,
    valueField: keyof T & string,
    disabledValues: string[] = [CONSTANTS.DISABLED]
  ): IRadioButtonState {
    const filtered = items.filter(item => this.ciEquals(item.governmentEntityFunded, entity));
    const isDisabled = filtered.some(item =>
      disabledValues.some(dv => this.ciEquals(item[valueField] as string, dv))
    );

    if (isDisabled) {
      return { yesEnabled: false, noEnabled: false, checked: null };
    }

    const hasYes = filtered.some(item => this.ciEquals(item[valueField] as string, CONSTANTS.YES));
    const hasNo = filtered.some(item => this.ciEquals(item[valueField] as string, CONSTANTS.NO));

    let checked: 'Yes' | 'No' | null = null;
    if (hasYes && !hasNo) {
      checked = CONSTANTS.YES;
    } else if (hasNo) {
      checked = CONSTANTS.NO;
    }

    return { yesEnabled: hasYes, noEnabled: hasNo, checked };
  }

  /**
   * Formats a Date as YYYY-MM-DD string to match desktop gateway format.
   */
  private formatDateForGateway(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // #endregion
}
